// frontend/src/pages/AccountPage.jsx

import { useEffect, useState, useRef } from "react";
import { Card, Typography, Collapse, Form, Button, Tag } from "antd";
import {
  UserOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  LockOutlined,
  PlusCircleOutlined,
  PrinterOutlined,
  ShoppingOutlined,
  ProfileOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { loadStripe } from "@stripe/stripe-js";
import { setUser } from "../../app.slice";
import { useFetchMeQuery } from "../home/home.service";
import {
  ADDITIONAL_LOCATION_PRICE,
  formatGbp,
} from "../../constant/pricing";
import { ROLE } from "../../constant";

// Import all components
import MembershipPanel from "./components/MembershipPanel";
import PaymentMethodPanel from "./components/PaymentMethodPanel";
import PasswordPanel from "./components/PasswordPanel";
import InvoicesPanel from "./components/InvoicesPanel";
import InvoiceDetailModal from "./components/InvoiceDetailModal";
import CancelSubscriptionModal from "./components/CancelSubscriptionModal";
import ProfileHeader from "./components/ProfileHeader";
import ProfilePanel from "./components/ProfilePanel";  // ✅ ADD THIS
import AddLocationPanel from "./components/AddLocationPanel";
import AddPrinterPanel from "./components/AddPrinterPanel";
import AddPrinterRollsPanel from "./components/AddPrinterRollsPanel";
import {
  useCancelSubscriptionMutation,
  useResumeSubscriptionMutation,
  useResubscribeSubscriptionMutation,
  useUpdatePasswordMutation,
  useUpdatePaymentMethodMutation,
} from "./account.service";
import {
  useGetAllInvoicesQuery,
  useGetInvoiceByIdQuery,
  useDownloadInvoiceMutation,
} from "./invoice.service";

const { Title, Text } = Typography;

/** SFBB brand green (matches sidenav / app accent) — icons/tags/buttons only */
const BRAND_GREEN = "#6DB33F";
const panelStyle = {
  backgroundColor: "white",
  borderRadius: "12px",
  marginBottom: "16px",
  border: "1px solid #e8e8e8",
};
const { Panel } = Collapse;

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    "pk_test_51RY3xZDw6TmwGTtebB5OiP6KcmZJLzDLIssD55qY3kXeEZyanHMRU03r29Yo8Q4jEs4DOVCyQEoiLy3QRKPFh9ej00zGlxXvOK"
);

const AccountPage = () => {
  // ==================== Form References ====================
  const [paymentForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // ==================== UI State ====================
  const [cancelModal, setCancelModal] = useState(false);
  const [activePanels, setActivePanels] = useState([]);
  const [isStripeInitialized, setIsStripeInitialized] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);

  // ==================== Stripe State ====================
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const stripeInitializedRef = useRef(false);

  // ==================== Redux State ====================
  const profile = useSelector((state) => state.app.user);
  const { refetch: refetchMe } = useFetchMeQuery();
  const isOwner =
    profile?.role === ROLE.RESTAURANT_ADMIN ||
    profile?.role === ROLE.SUPER_ADMIN;

  // ==================== Account Service Hooks ====================
  const [
    requestCancelSubscription,
    {
      isSuccess: isCancelSubscriptionSuccess,
      isLoading: isCancelSubscriptionLoading,
      data: cancelSubscriptionData,
    },
  ] = useCancelSubscriptionMutation();

  const [
    requestResumeSubscription,
    { isLoading: isResumeSubscriptionLoading },
  ] = useResumeSubscriptionMutation();

  const [
    requestResubscribeSubscription,
    { isLoading: isResubscribeLoading },
  ] = useResubscribeSubscriptionMutation();

  const [
    requestUpdatePaymentMethod,
    {
      isSuccess: isUpdatePaymentMethodSuccess,
      isLoading: isUpdatePaymentMethodLoading,
    },
  ] = useUpdatePaymentMethodMutation();

  const [
    requestUpdatePassword,
    { isSuccess: isUpdatePasswordSuccess, isLoading: isUpdatePasswordLoading },
  ] = useUpdatePasswordMutation();

  // ==================== Invoice Service Hooks ====================
  const {
    data: invoicesData,
    isLoading: loadingInvoices,
    refetch: refetchInvoices,
    error: invoicesError,
  } = useGetAllInvoicesQuery(undefined, {
    skip: !activePanels.includes("invoices"),
  });

  const {
    data: invoiceDetails,
    isLoading: loadingInvoiceDetails,
  } = useGetInvoiceByIdQuery(selectedInvoiceId, {
    skip: !selectedInvoiceId,
  });

  const [downloadInvoice, { isLoading: isDownloading }] = useDownloadInvoiceMutation();

  // ==================== Stripe Functions ====================
  const initializeStripe = async () => {
    if (stripeInitializedRef.current) return;
    try {
      const stripeInstance = await stripePromise;
      const elementsInstance = stripeInstance.elements();
      setStripe(stripeInstance);
      setElements(elementsInstance);
      stripeInitializedRef.current = true;
      setIsStripeInitialized(true);
    } catch (error) {
      console.error("Failed to initialize Stripe:", error);
    }
  };

  // ==================== Effect Hooks ====================
  useEffect(() => {
    const cameFromLoginRedirect = () => {
      const fromLogin = sessionStorage.getItem("fromLoginRedirect");
      if (fromLogin === "true") {
        setActivePanels(["membership"]);
        sessionStorage.removeItem("fromLoginRedirect");
      }
    };
    cameFromLoginRedirect();
  }, []);

  useEffect(() => {
    if (activePanels.includes("payment") && !stripeInitializedRef.current) {
      initializeStripe();
    }
  }, [activePanels]);

  useEffect(() => {
    if (activePanels.includes("payment") && isStripeInitialized && elements) {
      const timer = setTimeout(() => {
        const cardEl = document.getElementById("card-element");
        if (cardEl && !cardElement) {
          const cardElementInstance = elements.create("card", {
            style: {
              base: {
                fontSize: "16px",
                color: "#32325d",
                "::placeholder": { color: "#aab7c4" },
              },
              invalid: { color: "#fa755a", iconColor: "#fa755a" },
            },
          });
          cardElementInstance.mount("#card-element");
          setCardElement(cardElementInstance);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activePanels, isStripeInitialized, elements, cardElement]);

  useEffect(() => {
    return () => {
      if (cardElement) {
        cardElement.unmount();
        cardElement.destroy();
      }
    };
  }, [cardElement]);

  // ==================== Success Handlers ====================
  useEffect(() => {
    if (isCancelSubscriptionSuccess) {
      const updatedUser = cancelSubscriptionData?.data?.user;
      if (updatedUser) {
        dispatch(setUser(updatedUser));
      }
      toast.success(
        cancelSubscriptionData?.message ||
          "Subscription will cancel at the end of the billing period"
      );
      setCancelModal(false);
      refetchMe();
    }
  }, [isCancelSubscriptionSuccess, cancelSubscriptionData, dispatch, refetchMe]);

  useEffect(() => {
    const flag = searchParams.get("resubscribe");
    if (flag === "success") {
      toast.success("Subscription renewed successfully");
      refetchMe();
      searchParams.delete("resubscribe");
      setSearchParams(searchParams, { replace: true });
    } else if (flag === "cancel") {
      toast.info("Resubscribe checkout was cancelled");
      searchParams.delete("resubscribe");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetchMe]);

  // Deep-link: /account?panel=addLocation (from Locations page)
  useEffect(() => {
    const panel = searchParams.get("panel");
    if (panel === "addLocation" && isOwner) {
      setActivePanels((prev) =>
        prev.includes("addLocation") ? prev : [...prev, "addLocation"]
      );
      searchParams.delete("panel");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, isOwner]);

  useEffect(() => {
    if (isUpdatePaymentMethodSuccess) {
      toast.success("Payment method updated successfully");
      paymentForm.resetFields();
    }
  }, [isUpdatePaymentMethodSuccess]);

  useEffect(() => {
    if (isUpdatePasswordSuccess) {
      toast.success("Password updated successfully");
      passwordForm.resetFields();
    }
  }, [isUpdatePasswordSuccess]);

  // ==================== Event Handlers ====================
  const handlePanelChange = (keys) => {
    setActivePanels(keys);
  };

  const handleCancelSubscription = () => setCancelModal(true);
  const confirmCancelSubscription = () => requestCancelSubscription();

  const handleResumeSubscription = async () => {
    try {
      const result = await requestResumeSubscription().unwrap();
      if (result?.data?.user) {
        dispatch(setUser(result.data.user));
      }
      toast.success(result?.message || "Subscription resumed");
      refetchMe();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to keep plan");
    }
  };

  const handleResubscribe = async () => {
    try {
      const result = await requestResubscribeSubscription().unwrap();
      const url = result?.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      toast.error("Could not start resubscribe checkout");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to start resubscribe");
    }
  };

  const handlePaymentMethodUpdate = async (values) => {
    if (!stripe || !cardElement) {
      toast.error("Stripe not loaded");
      return;
    }
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: { name: values.cardholderName },
      });
      if (error) {
        toast.error(error.message);
        return;
      }
      const response = await requestUpdatePaymentMethod({
        paymentMethodId: paymentMethod.id,
      }).unwrap();
      if (response?.success) {
        toast.success(
          response?.message || "Payment method updated successfully"
        );
        paymentForm.resetFields();
        cardElement.clear();
      } else {
        toast.error(response?.message || "Failed to update payment method");
      }
    } catch (error) {
      console.error("Payment method update error:", error);
      toast.error("Network error. Please try again.");
    }
  };

  const handlePasswordUpdate = (values) => {
    if (values.newPassword !== values.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    requestUpdatePassword(values);
  };

  const handleViewDetails = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setInvoiceModalVisible(true);
  };

  const handleDownload = async (invoiceId) => {
    try {
      const blob = await downloadInvoice(invoiceId).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Invoice downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download invoice");
    }
  };

  const handleViewOnStripe = (hostedUrl) => {
    if (hostedUrl) {
      window.open(hostedUrl, "_blank");
    } else {
      toast.warning("No hosted URL available for this invoice");
    }
  };

  const handleLocationAdded = () => {
    toast.success("Location added successfully!");
    setActivePanels(activePanels.filter((key) => key !== "addLocation"));
    window.location.reload();
  };

  // ==================== Main Render ====================
  return (
    <Card className="account-page">
      {/* Page Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0, color: "#2D5016", fontSize: "32px" }}>
            Account
          </Title>
          <Text style={{ color: "#666", fontSize: "16px" }}>Membership details</Text>
        </div>
      </div>

      {/* Profile Header */}
      <ProfileHeader profile={profile} />

      {/* Collapse Panels */}
      <Collapse
        ghost
        style={{ backgroundColor: "transparent" }}
        expandIconPosition="end"
        activeKey={activePanels}
        onChange={handlePanelChange}
      >
        {/* ===== PANEL 1: PROFILE ===== */}
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <ProfileOutlined style={{ fontSize: "18px", color: BRAND_GREEN }} />
              <Text style={{ fontSize: "16px", fontWeight: "500", color: "#333" }}>
                Profile & Addresses
              </Text>
              <Tag
                style={{
                  marginLeft: 8,
                  color: BRAND_GREEN,
                  background: "#f3faf0",
                  borderColor: BRAND_GREEN,
                }}
              >
                Manage
              </Tag>
            </div>
          }
          key="profile"
          style={panelStyle}
        >
          <ProfilePanel />
        </Panel>

        {/* ===== PANEL 2: MEMBERSHIP ===== */}
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <UserOutlined style={{ fontSize: "18px", color: BRAND_GREEN }} />
              <Text style={{ fontSize: "16px", fontWeight: "500", color: "#333" }}>
                Manage membership
              </Text>
            </div>
          }
          key="membership"
          style={panelStyle}
        >
          <MembershipPanel
            profile={profile}
            isCancelSubscriptionLoading={isCancelSubscriptionLoading}
            isResumeSubscriptionLoading={isResumeSubscriptionLoading}
            isResubscribeLoading={isResubscribeLoading}
            handleCancelSubscription={handleCancelSubscription}
            handleResumeSubscription={handleResumeSubscription}
            handleResubscribe={handleResubscribe}
          />
        </Panel>

        {/* ===== PANEL 3: PAYMENT METHOD ===== */}
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <CreditCardOutlined style={{ fontSize: "18px", color: BRAND_GREEN }} />
              <Text style={{ fontSize: "16px", fontWeight: "500", color: "#333" }}>
                Manage payment method
              </Text>
            </div>
          }
          key="payment"
          style={panelStyle}
        >
          <PaymentMethodPanel
            paymentForm={paymentForm}
            profile={profile}
            isStripeInitialized={isStripeInitialized}
            activePanels={activePanels}
            isUpdatePaymentMethodLoading={isUpdatePaymentMethodLoading}
            stripe={stripe}
            cardElement={cardElement}
            handlePaymentMethodUpdate={handlePaymentMethodUpdate}
          />
        </Panel>

        {/* ===== PANEL 4: ORDER PRINTER (Stripe + MCF) ===== */}
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <PrinterOutlined style={{ fontSize: "18px", color: BRAND_GREEN }} />
              <Text style={{ fontSize: "16px", fontWeight: "500", color: "#333" }}>
                Order printer
              </Text>
            </div>
          }
          key="addPrinter"
          style={panelStyle}
        >
          <AddPrinterPanel
            onSuccess={() => {
              refetchMe?.();
            }}
          />
        </Panel>

        {/* ===== PANEL 5: ORDER PRINTER ROLLS (Stripe + MCF) ===== */}
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <ShoppingOutlined style={{ fontSize: "18px", color: BRAND_GREEN }} />
              <Text style={{ fontSize: "16px", fontWeight: "500", color: "#333" }}>
                Order label rolls
              </Text>
            </div>
          }
          key="addPrinterRolls"
          style={panelStyle}
        >
          <AddPrinterRollsPanel
            onSuccess={() => {
              refetchMe?.();
            }}
          />
        </Panel>

        {/* ===== PANEL 6: ADD LOCATION (owner only) ===== */}
        {isOwner && (
          <Panel
            header={
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <PlusCircleOutlined style={{ fontSize: "18px", color: BRAND_GREEN }} />
                <Text style={{ fontSize: "16px", fontWeight: "500", color: "#333" }}>
                  Add Location
                </Text>
                <Tag
                  style={{
                    marginLeft: 8,
                    color: BRAND_GREEN,
                    background: "#f3faf0",
                    borderColor: BRAND_GREEN,
                  }}
                >
                  +{formatGbp(ADDITIONAL_LOCATION_PRICE)}/mo
                </Tag>
              </div>
            }
            key="addLocation"
            style={panelStyle}
          >
            <AddLocationPanel
              onSuccess={handleLocationAdded}
              onCancel={() => {
                setActivePanels(activePanels.filter((key) => key !== "addLocation"));
              }}
            />
          </Panel>
        )}

        {/* ===== PANEL 7: INVOICES ===== */}
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FileTextOutlined style={{ fontSize: "18px", color: BRAND_GREEN }} />
              <Text style={{ fontSize: "16px", fontWeight: "500", color: "#333" }}>
                Invoices & Billing History
              </Text>
            </div>
          }
          key="invoices"
          style={panelStyle}
        >
          <InvoicesPanel
            loadingInvoices={loadingInvoices}
            invoicesError={invoicesError}
            invoicesData={invoicesData}
            refetchInvoices={refetchInvoices}
            isDownloading={isDownloading}
            handleViewDetails={handleViewDetails}
            handleDownload={handleDownload}
            handleViewOnStripe={handleViewOnStripe}
          />
        </Panel>

        {/* ===== PANEL 8: PASSWORD ===== */}
        <Panel
          header={
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <LockOutlined style={{ fontSize: "18px", color: BRAND_GREEN }} />
              <Text style={{ fontSize: "16px", fontWeight: "500", color: "#333" }}>
                Update password
              </Text>
            </div>
          }
          key="password"
          style={{ ...panelStyle, marginBottom: 0 }}
        >
          <PasswordPanel
            passwordForm={passwordForm}
            isUpdatePasswordLoading={isUpdatePasswordLoading}
            handlePasswordUpdate={handlePasswordUpdate}
          />
        </Panel>
      </Collapse>

      {/* Modals */}
      <CancelSubscriptionModal
        cancelModal={cancelModal}
        setCancelModal={setCancelModal}
        confirmCancelSubscription={confirmCancelSubscription}
        isLoading={isCancelSubscriptionLoading}
      />

      <InvoiceDetailModal
        invoiceModalVisible={invoiceModalVisible}
        setInvoiceModalVisible={setInvoiceModalVisible}
        setSelectedInvoiceId={setSelectedInvoiceId}
        invoiceDetails={invoiceDetails}
        loadingInvoiceDetails={loadingInvoiceDetails}
        isDownloading={isDownloading}
        selectedInvoiceId={selectedInvoiceId}
        handleViewOnStripe={handleViewOnStripe}
        handleDownload={handleDownload}
      />
    </Card>
  );
};

export default AccountPage;